<template>
  <div class="sales-panel">
    <div v-if="loading" class="state">加载中...</div>
    <div v-else-if="error" class="state error">{{ error }}</div>
    <HealthSalesPanel v-else-if="isHealthView" :data="data"/>
    <template v-else>
      <section class="section">
        <div class="section-head">
          <h3>总体情况</h3>
          <small>{{ data.asOfLabel || '' }}</small>
        </div>
        <div class="metrics">
          <div class="metric-item red">
            <div class="metric-circle">
              <strong>{{ enrollmentValue }}</strong>
            </div>
            <span>招生报名量</span>
          </div>
          <div class="metric-item purple">
            <div class="metric-circle">
              <strong>{{ enrollmentRate }}<small>%</small></strong>
            </div>
            <span>招生完成率</span>
          </div>
          <div class="metric-item blue">
            <div class="metric-circle">
              <strong>{{ recoveryLabel }}</strong>
            </div>
            <span>招商回款额</span>
          </div>
          <div class="metric-item green">
            <div class="metric-circle">
              <strong>{{ recoveryRate }}<small>%</small></strong>
            </div>
            <span>招商完成率</span>
          </div>
        </div>
      </section>

      <section class="section">
        <h3>具体情况</h3>
        <div class="table-wrap">
          <table class="detail-table">
            <thead>
              <tr>
                <th>销售人员</th>
                <th>招生</th>
                <th>回款</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in employees" :key="row.employeeId">
                <td>{{ row.employeeName }}</td>
                <td>{{ formatEnrollment(row.studentEnrollments) }}</td>
                <td>{{ row.recoveredLabel || '0' }}</td>
              </tr>
              <tr v-if="!employees.length">
                <td colspan="3" class="empty-cell">暂无销售数据</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section class="section">
        <h3>动态分析</h3>
        <div class="chart-block">
          <h4>招生完成情况(当月)</h4>
          <div ref="enrollChart" class="chart"></div>
        </div>
        <div class="chart-block">
          <h4>回款完成情况(当月)</h4>
          <div ref="recoveryChart" class="chart"></div>
        </div>
      </section>
    </template>
  </div>
</template>

<script>
import coreapi from '@/utils/coreapi';
import HealthSalesPanel from './HealthSalesPanel.vue';

const DIVISION_TABS = [
  { code: '安全事业部', label: '安全事业部' },
  { code: '传媒事业部', label: '文化事业部' },
  { code: '健康事业部', label: '健康事业部' }
];

const LINE_COLORS = ['#e32b32', '#2697e8', '#18a957'];
const HEALTH_UNIT = '健康事业部';

export default {
  name: 'SalesPerformancePanel',
  components: { HealthSalesPanel },
  props: {
    date: { type: String, default: '' },
    unit: { type: String, default: '安全事业部' }
  },
  data() {
    return {
      loading: false,
      error: '',
      data: {
        viewType: 'STANDARD',
        summary: {},
        employees: [],
        enrollmentTrend: [],
        recoveryTrend: []
      }
    };
  },
  computed: {
    activeUnit() {
      return this.unit || '安全事业部';
    },
    isHealthView() {
      return this.data.viewType === 'HEALTH' || this.activeUnit === HEALTH_UNIT;
    },
    summary() {
      return this.data.summary || {};
    },
    employees() {
      return this.data.employees || [];
    },
    enrollmentValue() {
      return this.formatEnrollment(this.summary.studentEnrollments);
    },
    enrollmentRate() {
      return Math.round(Number(this.summary.studentEnrollmentRate || 0));
    },
    recoveryLabel() {
      return this.summary.recoveredLabel || '0';
    },
    recoveryRate() {
      return Math.round(Number(this.summary.recoveredRate || 0));
    }
  },
  watch: {
    date() {
      this.load();
    },
    unit() {
      this.load();
    }
  },
  mounted() {
    this.load();
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
    this.disposeCharts();
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = '';
      this.disposeCharts();
      try {
        const asOf = this.date || this.today();
        this.data = await coreapi.bossDepartmentSales(asOf, this.activeUnit);
      } catch (e) {
        this.error = e.message || '加载失败';
        this.data = { viewType: 'STANDARD', summary: {}, employees: [], enrollmentTrend: [], recoveryTrend: [] };
      } finally {
        this.loading = false;
        this.$nextTick(() => {
          if (!this.isHealthView) this.renderCharts();
        });
      }
    },
    today() {
      const d = new Date();
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    },
    formatEnrollment(v) {
      if (v === null || v === undefined) return '0';
      return Math.round(Number(v)).toString();
    },
    initChart(ref) {
      const el = this.$refs[ref];
      if (!el) return null;
      const chart = this.$echarts.init(el);
      el.__chart = chart;
      return chart;
    },
    disposeCharts() {
      ['enrollChart', 'recoveryChart'].forEach(key => {
        const el = this.$refs[key];
        if (el && el.__chart) {
          el.__chart.dispose();
          el.__chart = null;
        }
      });
    },
    handleResize() {
      ['enrollChart', 'recoveryChart'].forEach(key => {
        const el = this.$refs[key];
        if (el && el.__chart) el.__chart.resize();
      });
    },
    renderCharts() {
      this.renderTrendChart('enrollChart', this.data.enrollmentTrend || [], false);
      this.renderTrendChart('recoveryChart', this.data.recoveryTrend || [], true);
    },
    renderTrendChart(ref, points, wanMode) {
      const chart = this.initChart(ref);
      if (!chart) return;
      if (!points.length) {
        chart.setOption({
          title: { text: '暂无数据', left: 'center', top: 'center', textStyle: { color: '#bbb', fontSize: 13 } }
        });
        return;
      }
      const topNames = (this.employees.slice(0, 2).map(r => r.employeeName) || []).filter(Boolean);
      const seriesNames = [...topNames, '总数'];
      const dates = points.map(p => String(p.date).slice(5));
      const series = seriesNames.map(name => {
        const key = name === '总数' ? 'total' : name;
        return {
          name,
          type: 'line',
          smooth: true,
          symbol: 'circle',
          symbolSize: 4,
          lineStyle: { width: 2 },
          data: points.map(p => Number(p[key] || 0))
        };
      });
      chart.setOption({
        color: LINE_COLORS,
        tooltip: {
          trigger: 'axis',
          formatter: params => {
            const lines = [params[0].axisValue];
            params.forEach(p => {
              const val = wanMode ? `${p.value}w` : p.value;
              lines.push(`${p.marker}${p.seriesName}: ${val}`);
            });
            return lines.join('<br/>');
          }
        },
        legend: {
          data: seriesNames,
          bottom: 0,
          itemWidth: 14,
          itemHeight: 8,
          textStyle: { fontSize: 11, color: '#666' }
        },
        grid: { left: 42, right: 16, top: 24, bottom: 36 },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: dates,
          axisLabel: { fontSize: 10, color: '#999' },
          axisLine: { lineStyle: { color: '#ddd' } }
        },
        yAxis: {
          type: 'value',
          axisLabel: {
            fontSize: 10,
            color: '#999',
            formatter: wanMode ? v => `${v}w` : undefined
          },
          splitLine: { lineStyle: { color: '#f0f0f0' } }
        },
        series
      });
    }
  }
};
</script>

<style lang="less" scoped>
.sales-panel {
  padding-bottom: 24px;
}

.division-tabs {
  display: flex;
  background: #fff;
  border-top: 1px solid #f0f1f5;
  padding: 0 8px;

  button {
    flex: 1;
    border: 0;
    background: none;
    padding: 14px 4px;
    color: #697386;
    font-size: 15px;

    &.active {
      color: #d9272e;
      border-bottom: 2px solid #d9272e;
      font-weight: 600;
    }
  }
}

.section {
  margin-top: 10px;
  background: #fff;
  padding: 14px 12px 16px;
}

.section-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 8px;

  h3 {
    margin: 0;
    font-size: 16px;
    color: #303133;
  }

  small {
    color: #9aa1b2;
    font-size: 11px;
  }
}

.section > h3 {
  margin: 0 0 10px;
  font-size: 16px;
  color: #303133;
}

.metrics {
  display: flex;
  justify-content: space-around;
  padding: 8px 0 4px;

  .metric-item {
    display: flex;
    flex-direction: column;
    align-items: center;

    .metric-circle {
      width: 72px;
      height: 72px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #fff;

      strong {
        font-size: 22px;
        font-weight: 700;
      }

      small {
        font-size: 12px;
        margin-left: 1px;
      }
    }

    span {
      margin-top: 8px;
      font-size: 12px;
      color: #666;
      text-align: center;
    }

    &.red .metric-circle { background: linear-gradient(135deg, #ff7e7e, #ff4d4f); }
    &.purple .metric-circle { background: linear-gradient(135deg, #c084fc, #a855f7); }
    &.blue .metric-circle { background: linear-gradient(135deg, #7aa8ff, #5b7cff); }
    &.green .metric-circle { background: linear-gradient(135deg, #4ade80, #16a34a); }
  }
}

.table-wrap {
  overflow-x: auto;
}

.detail-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;

  th, td {
    border: 1px solid #e8ebf0;
    padding: 10px 8px;
    text-align: center;
  }

  th {
    background: #4a4f63;
    color: #fff;
    font-weight: 500;
  }

  td {
    color: #303133;
  }

  .empty-cell {
    color: #9aa1b2;
  }
}

.chart-block {
  margin-top: 12px;

  h4 {
    margin: 0 0 6px;
    font-size: 14px;
    color: #41475a;
    font-weight: 500;
  }
}

.chart {
  width: 100%;
  height: 220px;
}

.state {
  padding: 60px 20px;
  text-align: center;
  color: #9aa1b2;

  &.error {
    color: #d9272e;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/boss/SalesPerformancePanel.vue